import {
  Auction,
  CreateAuctionPayload,
  PaginatedAuctionsResponse,
} from "./types";
import { authFetch } from "./utils/authFetch";
import { toast } from "react-toastify";

export const useAuctionApi = () => {
  const selectBidAsWinner = async (
    auctionId: string,
    bidId: string,
    showToast = true,
  ) => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/${auctionId}/select-winner`,
      {
        method: "POST",
        body: JSON.stringify({ bidId }),
      },
    );

    if (!res.ok) {
      const msg = await res.text();
      if (showToast) toast.error(msg || "Failed to select winner");
      throw new Error(msg || "Failed to select winner");
    }

    if (showToast) toast.success("Winner selected successfully");
  };

  const fetchPublicRecentAuctions = async (
    take = 20,
    showToast = false,
  ): Promise<Auction[]> => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/public/recent?take=${take}`,
    );

    if (!response.ok) {
      const err = await response.text();
      if (showToast) toast.error(err || "Failed to fetch auctions");
      throw new Error(err || "Failed to fetch auctions");
    }

    return await response.json();
  };

  const fetchAuctions = async (
    page = 1,
    pageSize = 20,
    showToast = false,
  ): Promise<PaginatedAuctionsResponse> => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction?page=${page}&pageSize=${pageSize}`,
    );

    if (!response.ok) {
      const err = await response.text();
      if (showToast) toast.error(err || "Failed to fetch auctions");
      throw new Error(err || "Failed to fetch auctions");
    }

    return await response.json();
  };

  const createAuction = async (
    payload: CreateAuctionPayload,
    showToast = true,
  ): Promise<any> => {
    const response = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction`,
      {
        method: "POST",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      const errText = await response.text();

      if (showToast) toast.error(errText || "Failed to create auction");
      throw new Error(
        errText ||
          `Failed to create auction: ${response.status} ${response.statusText}`,
      );
    }

    if (showToast) toast.success("Auction created successfully");
    return await response.json();
  };

  const fetchHistory = async (showToast = false): Promise<any[]> => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/history`,
    );

    if (!res.ok) {
      const errorText = await res.text();
      if (showToast) toast.error(errorText || "Failed to fetch history");
      throw new Error(errorText || "Failed to fetch history");
    }

    return await res.json();
  };

  const fetchUserAuctions = async (showToast = false): Promise<Auction[]> => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/user`,
    );

    if (!res.ok) {
      const errorText = await res.text();
      if (showToast) toast.error(errorText || "Failed to fetch your auctions");
      throw new Error(errorText || "Failed to fetch your auctions");
    }

    return await res.json();
  };

  const cancelAuction = async (id: string, showToast = true): Promise<void> => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/${id}/cancel`,
      {
        method: "POST",
      },
    );

    if (!res.ok) {
      const error = await res.text();
      if (showToast) toast.error(error || "Failed to cancel auction");
      throw new Error(error || "Failed to cancel auction");
    }

    if (showToast) toast.success("Auction cancelled");
  };

  const completeAuction = async (auctionId: string, showToast = true) => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/${auctionId}/complete`,
      {
        method: "POST",
      },
    );

    const msg = await res.text();
    if (!res.ok) {
      if (showToast) toast.error(msg || "Failed to complete auction");
      throw new Error(msg || "Failed to complete auction");
    }

    if (showToast) toast.success("Auction marked as completed");
  };

  const fetchRecentlyFinished = async (take = 3, showToast = false) => {
    const res = await authFetch(
      `${process.env.REACT_APP_API_URL}/api/Auction/recently-finished?take=${take}`,
    );

    if (!res.ok) {
      const errorText = await res.text();
      if (showToast)
        toast.error(errorText || "Failed to fetch finished auctions");
      throw new Error(errorText || "Failed to fetch finished auctions");
    }

    return await res.json();
  };

  return {
    selectBidAsWinner,
    fetchAuctions,
    fetchPublicRecentAuctions,
    createAuction,
    fetchHistory,
    fetchUserAuctions,
    cancelAuction,
    completeAuction,
    fetchRecentlyFinished,
  };
};
